import { useState } from 'react';
import HostDashboard from '../components/host/HostDashboard';
import QuizCreator from '../components/host/QuizCreator';
import QuizPreview from '../components/host/QuizPreview';
import SessionControl from '../components/host/SessionControl';
import SessionReview from '../components/host/SessionReview';
import { SessionProvider } from '../context/SessionContext';
import { createSession } from '../services/sessionService';
import { duplicateQuiz } from '../services/quizService';

export default function HostPage() {
  const [view, setView] = useState('dashboard'); // 'dashboard' | 'create' | 'preview' | 'session' | 'review'
  const [quiz, setQuiz] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [reviewSessionId, setReviewSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleCreateNew = () => {
    setQuiz(null);
    setView('create');
  };

  const handleEditQuiz = (quizToEdit) => {
    setQuiz(quizToEdit);
    setView('create');
  };

  const handleHostGame = async (quizToHost) => {
    setQuiz(quizToHost);
    setLoading(true);
    setError(null);

    try {
      const session = await createSession(quizToHost.id);
      setSessionId(session.id);
      setView('session');
    } catch (err) {
      console.error('Error creating session:', err);
      setError(err.message || 'Klarte ikkje opprette økt');
    } finally {
      setLoading(false);
    }
  };

  const handleQuizSaved = (savedQuiz) => {
    setQuiz(savedQuiz);
    // Stay on 'create' view - don't navigate away
  };

  const handlePreviewQuiz = (quizToPreview) => {
    setQuiz(quizToPreview);
    setView('preview');
  };

  const handleStartSession = async () => {
    setLoading(true);
    setError(null);

    try {
      const session = await createSession(quiz.id);
      setSessionId(session.id);
      setView('session');
    } catch (err) {
      console.error('Error creating session:', err);
      setError(err.message || 'Klarte ikkje opprette økt');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToEdit = () => {
    setView('create');
  };

  const handleBackToDashboard = () => {
    setQuiz(null);
    setSessionId(null);
    setReviewSessionId(null);
    setView('dashboard');
  };

  const handleOpenSession = (sid) => {
    setReviewSessionId(sid);
    setView('review');
  };

  const handleCopyQuiz = async (quiz) => {
    try {
      await duplicateQuiz(quiz.id);
      setView('dashboard');
    } catch (err) {
      alert('Klarte ikkje kopiere kviss: ' + err.message);
    }
  };

  // Show loading state while creating session
  if (loading) {
    return (
      <div className="min-h-screen bg-mp-bg-lightgreen font-body flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mp-magenta mx-auto mb-4"></div>
          <h2 className="font-display text-xl text-mp-darkgreen">Opprettar økt …</h2>
        </div>
      </div>
    );
  }

  // Show error if session creation failed
  if (error) {
    return (
      <div className="min-h-screen bg-mp-bg-lightgreen font-body flex items-center justify-center p-4">
        <div className="bg-white border border-[rgba(163,0,97,0.20)] rounded-2xl p-6 max-w-md w-full shadow-mp-1">
          <h2 className="font-display text-xl text-mp-magenta mb-2">Feil</h2>
          <p className="font-body text-mp-stone-700 mb-4">{error}</p>
          <button
            onClick={() => {
              setError(null);
              setView('dashboard');
            }}
            className="mp-cta w-full"
          >
            Tilbake til oversikta
          </button>
        </div>
      </div>
    );
  }

  // Show SessionReview if in review view
  if (view === 'review' && reviewSessionId) {
    return <SessionReview sessionId={reviewSessionId} onBack={handleBackToDashboard} />;
  }

  // Show SessionControl if in session view
  if (view === 'session' && sessionId) {
    return (
      <SessionProvider sessionId={sessionId}>
        <SessionControl sessionId={sessionId} onBack={handleBackToDashboard} />
      </SessionProvider>
    );
  }

  // Show QuizPreview if in preview view
  if (view === 'preview' && quiz) {
    return (
      <QuizPreview
        quiz={quiz}
        onStartSession={handleStartSession}
        onBack={handleBackToEdit}
      />
    );
  }

  // Show QuizCreator if in create view
  if (view === 'create') {
    return (
      <QuizCreator
        onSaved={handleQuizSaved}
        onPreview={handlePreviewQuiz}
        onClose={handleBackToDashboard}
        initialQuiz={quiz}
      />
    );
  }

  // Show Dashboard by default
  return (
    <HostDashboard
      onCreateNew={handleCreateNew}
      onHostGame={handleHostGame}
      onEditQuiz={handleEditQuiz}
      onOpenSession={handleOpenSession}
      onCopyQuiz={handleCopyQuiz}
    />
  );
}
